<template>
  <!-- Same structure and classes as charge_row.vue, so the payments dashboard's
       two feeds share one desktop table and one stacked phone card. -->
  <tbody class="charge-row subscription-event-row">
    <tr class="charge-row__main">
      <td class="charge-row__status">
        <div class="charge-row__status-line">
          <span class="charge-badge" :class="`charge-badge--${eventTone}`"><i :class="eventIcon" aria-hidden="true"></i> {{ eventTypeDisplay }}</span>
        </div>
        <div v-if="eventDetail" class="charge-row__detail" :class="`charge-row__detail--${eventTone === 'danger' ? 'danger' : 'neutral'}`">{{ eventDetail }}</div>
      </td>
      <td class="charge-row__for">
        <div class="charge-row__title">{{ productNames(row.billing_subscription) || 'Subscription' }}</div>
        <div class="charge-row__meta">{{ cleanPlanNames(row.billing_subscription) }}</div>
        <div v-if="coupon" class="charge-row__coupon"><coupon-pill :code="coupon.code"></coupon-pill></div>
        <div v-if="wrestlerProfiles.length > 0" class="charge-row__for-wrestlers">
          <template v-for="(wrestler, index) in wrestlerProfiles"><template v-if="index > 0">, </template><a :key="wrestler.id" :href="`/wrestlers/${wrestler.id}`" target="_blank">{{ wrestler.full_name }}</a></template>
        </div>
      </td>
      <td class="charge-row__amount">
        <div class="charge-row__amount-line">
          <span>{{ formatAsCurrency(subscriptionTotal(row.billing_subscription)) }}<span class="charge-row__interval">{{ subInterval(row.billing_subscription) }}</span></span>
        </div>
      </td>
      <td class="charge-row__billed-to">
        <a v-if="billableUrl" :href="billableUrl" target="_blank">{{ displayNameOrEmail }}</a>
        <template v-else>{{ displayNameOrEmail }}</template>
      </td>
      <td class="charge-row__wrestlers">
        <template v-if="wrestlerProfiles.length > 0">
          <div v-for="wrestler in wrestlerProfiles" :key="wrestler.id">
            <a :href="`/wrestlers/${wrestler.id}`" target="_blank">{{ wrestler.full_name }}</a>
          </div>
        </template>
        <span v-else class="charge-row__none">—</span>
      </td>
      <td class="charge-row__date">
        <span>{{ shortDate(row.created_at) }}</span>
        <span class="charge-row__time">{{ formatWithMoment(row.created_at, 'h:mm A') }}</span>
        <span class="charge-row__date-payer"> · {{ row.event_name === 'payment.failed' ? 'Billed to' : 'Paid by' }} <a v-if="billableUrl" :href="billableUrl" target="_blank">{{ displayNameOrEmail }}</a><template v-else>{{ displayNameOrEmail }}</template></span>
      </td>
      <td class="charge-row__actions">
        <div class="charge-row__buttons">
          <a v-if="showViewSubscription" :href="`/subscriptions/${row.billing_subscription.billing_partner_id}`" target="_blank" class="btn btn-default btn-compact">View subscription</a>
          <!-- Once there's a note, it's edited from the note line -->
          <button v-if="noteEditable && !editingNotes && !row.notes" type="button" class="btn btn-default btn-compact" @click="editingNotes = true">
            <i class="fa-regular fa-note-sticky" aria-hidden="true"></i> Add note
          </button>
        </div>
      </td>
    </tr>
    <!-- The note on its own line, starting under For -->
    <tr v-if="row.notes && !editingNotes" class="charge-row__note-row">
      <td class="charge-row__note-spacer"></td>
      <td colspan="6"><row-note :text="row.notes" :label="noteSourceLabel ? `Note ${noteSourceLabel}` : null" :editable="noteEditable" @edit="editingNotes = true"></row-note></td>
    </tr>
    <tr v-if="noteEditable && editingNotes" class="charge-row__refund">
      <td colspan="7">
        <div class="form-group subscription-event-row__note-form">
          <textarea v-model="draftNotes"
                    class="form-control"
                    rows="2"
                    style="resize: none"
                    placeholder="Add notes here.."></textarea>
          <div class="flex gap-xs m-t-xs">
            <ladda-button @lbClicked="save" el-class="btn-primary" :loading="saving">
              Save
            </ladda-button>
            <button v-show="!saving" type="button" class="btn btn-default" @click="editingNotes = false">
              Cancel
            </button>
          </div>
          <p v-if="error" class="text-danger m-t-xs text-12">
            Error saving note
          </p>
        </div>
      </td>
    </tr>
  </tbody>
</template>

<script>
import { formattingMixin } from './mixins/formatting_mix';
import { billingPlanMix } from './mixins/billing_plan_mix';
import LaddaButton from '../../shared/ladda_button.vue';
import CouponPill from './coupon_pill.vue';
import RowNote from './row_note.vue';

export default {
  name: 'subscription-event-row',
  components: {
    LaddaButton,
    CouponPill,
    RowNote,
  },
  mixins: [formattingMixin, billingPlanMix],
  props: {
    row: {
      type: Object,
      required: true,
    },
    noteEditable: {
      type: Boolean,
      default: false,
    },
    showViewSubscription: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      editingNotes: false,
      draftNotes: this.row.notes || '',
      saving: false,
      error: false,
    };
  },
  computed: {
    // Who supplied the note, derived from the profile that triggered the event.
    // Coach-initiated cancels read as "team admin"; subscriber self-cancels as "subscriber".
    noteSourceLabel() {
      if (!this.row.notes) return '';
      if (this.row.profile_type === 'CoachProfile') return 'from team admin';
      if (this.row.profile_type === 'ParentProfile' || this.row.profile_type === 'WrestlerProfile') return 'from subscriber';
      return '';
    },
    coupon() {
      return _.first(this.row.billing_subscription?.coupons);
    },
    wrestlerProfiles() {
      const items = _.filter(this.row.billing_subscription.billing_plan_subscription_items, (si) => {
        const plan = this.planForItem(si, this.row.billing_subscription);
        return plan && !plan.is_wiq_fee && !plan.is_tax_item;
      });

      return _.uniqBy(_.flatMap(items, (si) => si.wrestler_profiles), 'id');
    },
    billableUrl() {
      const { billable } = this.row.billing_subscription.billing_profile;
      switch (billable?.type) {
        case 'ParentProfile':
          return `/parents/${billable.id}`;
        case 'CoachProfile':
          return `/coaches/${billable.id}`;
        default:
          return null;
      }
    },
    displayNameOrEmail() {
      const profile = this.row.billing_subscription.billing_profile;
      return profile.billable?.display_name || profile.email;
    },
    // Distinguishes the three flavors of a subscription.canceled event so the timeline can read
    // "Expired" vs "Canceled" vs "Canceled · Unpaid". Prefers the server-stamped cause; for rows
    // canceled before that column shipped, falls back to inferring from the actor / expiring contract.
    canceledCause() {
      if (this.row.event_name !== 'subscription.canceled') return null;
      if (this.row.subscription_cancellation_cause) return this.row.subscription_cancellation_cause;
      if (this.row.profile_id) return 'member_or_admin';
      if (this.row.billing_subscription?.expires_on) return 'expired';
      return 'payment_failure';
    },
    // Secondary line under the event label: the decline reason for payment events, or a plain-English
    // explanation of a system-initiated cancellation (expired contract / repeated payment failures).
    eventDetail() {
      if (this.row.event_name === 'payment.failed' || this.row.event_name === 'payment.recovered') {
        return this.row.log;
      }
      if (this.canceledCause === 'payment_failure') {
        return 'Automatically canceled after repeated failed payments.';
      }
      if (this.canceledCause === 'expired') {
        return 'Membership contract reached its end date.';
      }
      return null;
    },
    eventTypeDisplay() {
      if (this.row.event_name === 'subscription.canceled') {
        if (this.canceledCause === 'expired') return 'Expired';
        if (this.canceledCause === 'payment_failure') return 'Canceled · Unpaid';
        return 'Canceled';
      }
      const displays = {
        'subscription.created': 'Created',
        'subscription.renewed': 'Renewed',
        'bill_date.changed': 'Bill Date Changed',
        'payments.paused': 'Payments Paused',
        'payments.resumed': 'Payments Resumed',
        'plan.changed': 'Tier Changed',
        'payment.failed': 'Payment Failed',
        'payment.recovered': 'Payment Recovered',
      };
      return displays[this.row.event_name] || this.row.event_name;
    },
    // Badge tone, same meaning as before: green success, red cancel, gray
    // expired, yellow changes / recoverable failures, blue informational.
    eventTone() {
      if (this.row.event_name === 'subscription.canceled') {
        return this.canceledCause === 'expired' ? 'neutral' : 'danger';
      }
      const tones = {
        'subscription.created': 'success',
        'subscription.renewed': 'success',
        'bill_date.changed': 'warning',
        'payments.paused': 'warning',
        'payments.resumed': 'info',
        'plan.changed': 'info',
        'payment.failed': 'warning',
        'payment.recovered': 'success',
      };
      return tones[this.row.event_name] || 'neutral';
    },
    eventIcon() {
      const icons = {
        'subscription.created': 'fa-solid fa-plus',
        'subscription.renewed': 'fa-solid fa-arrows-rotate',
        'subscription.canceled': 'fa-solid fa-xmark',
        'bill_date.changed': 'fa-solid fa-pencil',
        'payments.paused': 'fa-solid fa-pause',
        'payments.resumed': 'fa-solid fa-play',
        'plan.changed': 'fa-solid fa-right-left',
        'payment.failed': 'fa-solid fa-triangle-exclamation',
        'payment.recovered': 'fa-solid fa-check',
      };
      return icons[this.row.event_name] || 'fa-solid fa-circle';
    },
  },
  methods: {
    shortDate(value) {
      const date = moment(value);
      return date.year() === moment().year() ? date.format('MMM D') : date.format('MMM D, YYYY');
    },
    save: _.throttle(function () {
      const vm = this;
      if (vm.saving) return;

      vm.saving = true;
      vm.error = false;

      axios.put(`/api/v1/billing_subscription_events/${vm.row.id}`, {
        billing_subscription_event: {
          notes: vm.draftNotes,
        },
      }).then(() => {
        vm.row.notes = vm.draftNotes;
        vm.editingNotes = false;
        vm.saving = false;
        vm.$notificationManager.emit('show-toast', 'Note saved successfully', true);
      }).catch(() => {
        vm.error = true;
        vm.saving = false;
      });
    }, 500),
  },
};
</script>
